import React, { useMemo } from 'react';
import i18n from '../../i18n';
import { SvgTv } from '../../icons';
import { Asset, SeriesAsset } from '../../queries/ats/titles/types/asset';
import { SeriesSource, Source } from '../../queries/ats/titles/types/sources';
import { InputContext } from './use-input-context';
import { getPreferredSource } from './utils';

type AvailableContextType = 'tv' | 'vod' | 'svod' | 'thirdparty' | 'link';

interface AvailableContextOptions {
  id: string;
  name: string;
  image: string | JSX.Element;
  type: AvailableContextType;
  sources: Array<Source | SeriesSource>;
}

export class AvailableContext {
  public id: string;
  public name: string;
  public image: string | JSX.Element;
  public type: AvailableContextType;
  private _sources: Array<Source | SeriesSource>;

  constructor(options: AvailableContextOptions) {
    this.id = options.id;
    this.name = options.name;
    this.image = options.image;
    this.type = options.type;
    this._sources = options.sources;
  }

  public sources(sources: Source[]) {
    if (this.type === 'tv') {
      return sources.filter((source) => source.type === 'program');
    }

    return sources.filter((source) => this.isActive(source.id));
  }

  public episodes(asset: SeriesAsset) {
    if (this.type === 'tv') {
      return asset.episodes
        .filter((episode) => episode.sources.some((source) => source.type === 'program'))
        .filter((episode) => episode.sources.length > 0);
    }

    return asset.episodes
      .filter((episode) => episode.sources.some((source) => this.isActive(source.id)))
      .filter((episode) => episode.sources.length > 0);
  }

  public preferredSource(asset: Exclude<Asset, SeriesAsset>, inputContext: InputContext) {
    const sources = this.sources(asset.sources);
    return getPreferredSource(sources, this, inputContext);
  }

  public isActive(contextId: string) {
    return contextId === this.id || this._sources.some((source) => source.id === contextId);
  }

  public hasPlatformTitleId(platformTitleId: string) {
    return this._sources.some((source) => {
      if ('platformTitleId' in source) {
        return source.platformTitleId === platformTitleId;
      }

      return false;
    });
  }

  public hasProviderId(providerId: string) {
    return this._sources.some((source) => {
      if ('providerIdentifier' in source) {
        return source.providerIdentifier === providerId;
      }

      return false;
    });
  }

  public hasServiceType(serviceType: string) {
    return this._sources.some((source) => {
      return source.type === serviceType;
    });
  }
}

function createTvContext(sources: Array<Source | SeriesSource>) {
  return new AvailableContext({
    id: 'tv',
    name: i18n.t('details:on-tv'),
    image: <SvgTv />,
    type: 'tv',
    sources,
  });
}

function createContext(sources: Array<Source | SeriesSource>) {
  const firstSource = sources[0];

  if (firstSource.type === 'program') {
    return createTvContext(sources);
  }

  // In theory any type other than 'program' should only have
  // one source, but we'll handle multiple sources just in case.
  return new AvailableContext({
    id: firstSource.id,
    name: firstSource.name,
    image: firstSource.image,
    type: firstSource.type,
    sources: sources,
  });
}

function moveObject<T>(arr: T[], targetKey: keyof T, targetValue: T[keyof T], newIndex: number) {
  const target = arr.find((value) => value[targetKey] === targetValue);
  const newArray = arr.filter((value) => value[targetKey] !== targetValue);

  if (target) {
    newArray.splice(newIndex, 0, target);
  }

  return newArray;
}

function groupSources(sources: Array<Source | SeriesSource>) {
  return sources.reduce((acc, source) => {
    const id = source.type === 'program' ? 'tv' : source.id;
    const values = acc.get(id) || [];
    values.push(source);
    acc.set(id, values);
    return acc;
  }, new Map<string, Array<Source | SeriesSource>>());
}

export function createContexts(asset: Asset) {
  const groupedSources = groupSources(asset.sources);
  const values = Object.values<Array<Source | SeriesSource>>([...groupedSources.values()]);
  const contexts = values.map((sources) => createContext(sources));

  const firstProgramIndex = asset.sources.findIndex((source) => source.type === 'program');

  return moveObject(contexts, 'type', 'program', firstProgramIndex);
}

export function useAvailableContexts(asset: Asset) {
  return useMemo(() => createContexts(asset), [asset]);
}
